/**
 * Hook to fetch published storybooks for parents
 * Uses type-safe transformers to convert DB data to app types
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase/client';
import { 
  transformStorybook, 
  transformStorybooks,
  transformStorybookPages,
} from '../../../lib/supabase/index';
import type { Storybook, StorybookPage } from '../../../lib/supabase/transforms';

// Re-export types for component use  
export type { Storybook as PublishedStorybook, StorybookPage };

interface UsePublishedStorybooksReturn {
  storybooks: Storybook[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  viewingAs: string | null;
  viewingAsName: string | null;
}

interface UseStorybookBySlugReturn {
  storybook: Storybook | null;
  pages: StorybookPage[];
  loading: boolean;
  error: string | null;
}

/**
 * Fetch all storybooks accessible to the current user
 * Supports admin "view as" mode via ?viewAs=parentId query param
 */
export function usePublishedStorybooks(): UsePublishedStorybooksReturn {
  const [searchParams] = useSearchParams();
  const viewAsParentId = searchParams.get('viewAs');
  
  const [storybooks, setStorybooks] = useState<Storybook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingAsName, setViewingAsName] = useState<string | null>(null);

  const fetchStorybooks = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check auth state
      const { data: { user } } = await supabase.auth.getUser();
      
      if (viewAsParentId) {
        // Admin viewing as specific parent
        console.log('[Storybooks] Viewing as parent:', viewAsParentId);
        
        // Get parent name
        const { data: parentProfile } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', viewAsParentId)
          .maybeSingle();
        
        if (parentProfile) {
          setViewingAsName(parentProfile.name);
        }

        // Get parent's accessible storybooks
        const { data: access } = await supabase
          .from('storybook_access')
          .select('storybook_id')
          .eq('parent_id', viewAsParentId);

        const storybookIds = (access || [])
          .map(a => a.storybook_id)
          .filter((id): id is string => id !== null);

        // Fetch public books + parent's private books
        const { data: publicBooks } = await supabase
          .from('published_storybooks')
          .select('*')
          .eq('is_published', true)
          .eq('is_public', true);

        let privateBooks: typeof publicBooks = [];
        if (storybookIds.length > 0) {
          const { data } = await supabase
            .from('published_storybooks')
            .select('*')
            .eq('is_published', true)
            .in('id', storybookIds);
          privateBooks = data || [];
        }

        // Merge and dedupe
        const allBooks = [...(publicBooks || []), ...privateBooks];
        const uniqueBooks = allBooks.filter((book, index, self) => 
          index === self.findIndex(b => b.id === book.id)
        );

        setStorybooks(transformStorybooks(uniqueBooks));
      } else if (user) {
        // Logged in parent - get their books
        const { data: profile } = await supabase
          .from('profiles')
          .select('id, is_parent')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profile?.is_parent) {
          // Get parent's accessible storybooks
          const { data: access } = await supabase
            .from('storybook_access')
            .select('storybook_id')
            .eq('parent_id', profile.id);

          const storybookIds = (access || [])
            .map(a => a.storybook_id)
            .filter((id): id is string => id !== null);

          // Fetch public books + parent's private books
          const { data: publicBooks } = await supabase
            .from('published_storybooks')
            .select('*')
            .eq('is_published', true)
            .eq('is_public', true);

          let privateBooks: typeof publicBooks = [];
          if (storybookIds.length > 0) {
            const { data } = await supabase
              .from('published_storybooks')
              .select('*')
              .eq('is_published', true)
              .in('id', storybookIds);
            privateBooks = data || [];
          }

          // Merge and dedupe
          const allBooks = [...(publicBooks || []), ...privateBooks];
          const uniqueBooks = allBooks.filter((book, index, self) => 
            index === self.findIndex(b => b.id === book.id)
          );

          setStorybooks(transformStorybooks(uniqueBooks));
        } else {
          // Non-parent user - show only public
          const { data } = await supabase
            .from('published_storybooks')
            .select('*')
            .eq('is_published', true)
            .eq('is_public', true);
          
          setStorybooks(transformStorybooks(data || []));
        }
      } else {
        // Not logged in - show only public
        const { data } = await supabase
          .from('published_storybooks')
          .select('*')
          .eq('is_published', true)
          .eq('is_public', true);
        
        setStorybooks(transformStorybooks(data || []));
      }
    } catch (err) {
      console.error('[Storybooks] Error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStorybooks();
  }, [viewAsParentId]);

  return { 
    storybooks, 
    loading, 
    error, 
    refetch: fetchStorybooks, 
    viewingAs: viewAsParentId, 
    viewingAsName 
  };
}

/**
 * Fetch a single storybook by slug with its pages
 */
export function useStorybookBySlug(slug: string | undefined): UseStorybookBySlugReturn {
  const [storybook, setStorybook] = useState<Storybook | null>(null);
  const [pages, setPages] = useState<StorybookPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  
  const viewAsParentId = searchParams.get('viewAs');

  useEffect(() => {
    const fetchStorybook = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        // Build query - if viewAs, don't require public
        let query = supabase
          .from('published_storybooks')
          .select('*')
          .eq('slug', slug)
          .eq('is_published', true);

        if (!viewAsParentId) {
          // Check if user has access
          const { data: { user } } = await supabase.auth.getUser();
          
          if (user) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('id')
              .eq('user_id', user.id)
              .maybeSingle();

            if (profile) {
              // Get the storybook first to check access
              const { data: book } = await query.maybeSingle();
              
              if (book && !book.is_public) {
                // Check if parent has access
                const { data: access } = await supabase
                  .from('storybook_access')
                  .select('id')
                  .eq('parent_id', profile.id)
                  .eq('storybook_id', book.id)
                  .maybeSingle();

                if (!access) {
                  setError('Access denied');
                  setLoading(false);
                  return;
                }
              }

              if (book) {
                setStorybook(transformStorybook(book));
                
                // Fetch pages
                const { data: pagesData } = await supabase
                  .from('storybook_pages')
                  .select('*')
                  .eq('storybook_id', book.id)
                  .order('page_number');

                setPages(transformStorybookPages(pagesData || []));
                setLoading(false);
                return;
              }
            }
          }
          
          // Fall back to public only
          query = query.eq('is_public', true);
        }

        const { data: bookData, error: bookError } = await query.maybeSingle();

        if (bookError) {
          setError(bookError.message);
          return;
        }

        if (!bookData) {
          setError('Storybook not found');
          return;
        }

        setStorybook(transformStorybook(bookData));

        // Fetch pages
        const { data: pagesData, error: pagesError } = await supabase
          .from('storybook_pages')
          .select('*')
          .eq('storybook_id', bookData.id)
          .order('page_number');

        if (pagesError) {
          setError(pagesError.message);
          return;
        }

        setPages(transformStorybookPages(pagesData || []));
      } catch (err) {
        console.error('[Storybook] Error:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchStorybook();
  }, [slug, viewAsParentId]);

  return { storybook, pages, loading, error };
}
